"use client";

import { useCallback, useEffect, useRef } from "react";

// useArtifactSave — debounced autosave for inline document editing (artifacts AND
// dimensions). Coalesces rapid keystrokes per document key and PATCHes the latest
// markdown to /api/os/artifact-document, which routes by key prefix (art: vs
// dim:). A dimension write additionally needs its projectId + raw name to target
// the right row — carried on the save target. Flushes anything pending on unmount
// (with keepalive) so an edit is never lost when you navigate away. A quiet
// "saved" Noticer flash rides the existing innernet:clip note channel.
//
// `flushNow` is the explicit-save path (the reader's save button): it cancels the
// pending debounce, pushes whatever is buffered immediately, and reports whether
// the write truly landed — so the UI shows "memory updated" only when earned.
//
// `discard` is cancel's half: drop the buffer + timer for a key so nothing
// pending ever lands. `landedAfter` says whether a write for that key reached
// the server since a given moment (the snapshot taken on entering edit mode),
// and `writeNow` pushes a body immediately through the same PATCH — the two
// together let cancel put the snapshot back when an autosave already fired.

export interface SaveTarget {
  key: string;
  projectId?: string;
  /** raw DB name for a dimension (art: writes ignore it). */
  name?: string;
}

export function useArtifactSave(debounceMs = 800) {
  const timers = useRef<Map<string, ReturnType<typeof setTimeout>>>(new Map());
  const pending = useRef<Map<string, { document: string; target: SaveTarget }>>(new Map());
  // when the last write for each key actually landed (Date.now()).
  const landed = useRef<Map<string, number>>(new Map());
  // the PATCH currently on the wire per key — cancel waits for it, so a write
  // that was already in flight can't land AFTER cancel decided nothing had.
  const inflight = useRef<Map<string, Promise<boolean>>>(new Map());

  const flush = useCallback((key: string, quiet = false): Promise<boolean> => {
    const entry = pending.current.get(key);
    if (!entry) return Promise.resolve(true); // nothing pending — already persisted
    const { document, target } = entry;
    const run = (async () => {
      try {
        const res = await fetch("/api/os/artifact-document", {
          method: "PATCH",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ key: target.key, projectId: target.projectId, name: target.name, document }),
          keepalive: true,
        });
        // fetch resolves on 4xx/5xx — only a truly-persisted write is a "save". On a
        // server error leave the buffer in place so the next edit / unmount retries,
        // and do NOT flash "saved".
        if (!res.ok) return false;
        landed.current.set(key, Date.now());
        // clear only if a newer edit didn't arrive while this was in flight.
        if (pending.current.get(key)?.document === document) pending.current.delete(key);
        // a restore (cancel putting the snapshot back) is not a "save" to announce
        if (!quiet && typeof window !== "undefined")
          window.dispatchEvent(new CustomEvent("innernet:clip", { detail: { action: "note", name: "saved" } }));
        return true;
      } catch {
        /* offline / network throw — leave it queued for the next save or unmount */
        return false;
      }
    })();
    inflight.current.set(key, run);
    // `run` never rejects (every path above returns), so this is a plain hook.
    void run.then(() => {
      if (inflight.current.get(key) === run) inflight.current.delete(key);
    });
    return run;
  }, []);

  const save = useCallback(
    (target: SaveTarget, document: string) => {
      pending.current.set(target.key, { document, target });
      const t = timers.current.get(target.key);
      if (t) clearTimeout(t);
      timers.current.set(
        target.key,
        setTimeout(() => {
          timers.current.delete(target.key);
          void flush(target.key);
        }, debounceMs),
      );
    },
    [debounceMs, flush],
  );

  const flushNow = useCallback(
    (key: string): Promise<boolean> => {
      const t = timers.current.get(key);
      if (t) {
        clearTimeout(t);
        timers.current.delete(key);
      }
      return flush(key);
    },
    [flush],
  );

  /** drop whatever is buffered for a key — cancel's first move — and wait for
   *  any write already on the wire, so `landedAfter` answers with the truth. */
  const discard = useCallback(async (key: string): Promise<void> => {
    const t = timers.current.get(key);
    if (t) {
      clearTimeout(t);
      timers.current.delete(key);
    }
    pending.current.delete(key);
    const live = inflight.current.get(key);
    if (live) await live.catch(() => false);
  }, []);

  /** did a write for this key land on the server after `since` (ms epoch)? */
  const landedAfter = useCallback((key: string, since: number): boolean => {
    const at = landed.current.get(key);
    return at !== undefined && at >= since;
  }, []);

  /** push a body immediately (no debounce) — the same PATCH the save uses. */
  const writeNow = useCallback(
    async (target: SaveTarget, document: string): Promise<boolean> => {
      await discard(target.key);
      pending.current.set(target.key, { document, target });
      return flush(target.key, true);
    },
    [discard, flush],
  );

  useEffect(() => {
    const timersMap = timers.current;
    return () => {
      for (const [key, t] of timersMap) {
        clearTimeout(t);
        void flush(key);
      }
    };
  }, [flush]);

  return { save, flushNow, discard, landedAfter, writeNow };
}
